import { DateTime } from 'luxon';

// Same convention as the RFM tab: a missing measurement is an em dash, and a
// real 0 never collapses into it.
import { EM_DASH } from './rfmAnalysis';

// The full byChannel key set the segmentPerformance endpoint can send, in the
// order the table's channel columns follow.
export const CHANNEL_ORDER = [
  'recommender',
  'banner',
  'email',
  'textMessage',
  'viberMessage',
  'pushNotification',
  'whatsappMessage',
  'salesAgent',
];

const CHANNEL_SORT_PREFIX = 'channel:';

/**
 * Sort key identifying a per-channel column, kept in one place so the header and
 * the value accessor cannot drift apart.
 * @param {string} channel
 * @returns {string}
 */
export const channelSortKey = (channel) => `${CHANNEL_SORT_PREFIX}${channel}`;

/**
 * Which channel columns to draw: the union of byChannel keys across the returned
 * segments, in CHANNEL_ORDER. Only channels with activity are present in a
 * segment and the set differs per segment, so eight always-on columns would be
 * mostly empty. A key the API has added since CHANNEL_ORDER was last updated
 * still gets a column, sorted after the known ones — dropping it would make that
 * channel's conversions disappear from the table rather than look unfamiliar.
 * @param {Array<{byChannel?: object}>} [segments]
 * @returns {string[]}
 */
export const activeChannelKeys = (segments) => {
  const present = new Set();
  for (const segment of segments || []) {
    for (const channel of Object.keys(segment.byChannel || {})) {
      present.add(channel);
    }
  }
  const unknown = [...present].filter((channel) => !CHANNEL_ORDER.includes(channel)).sort();
  return [...CHANNEL_ORDER.filter((channel) => present.has(channel)), ...unknown];
};

/**
 * Value a segment sorts by for a given column. Channel columns sort on the
 * channel's monetary value; a segment with no activity on that channel has no
 * value at all, which is not the same as zero.
 * @param {object} segment - one entry of the segmentPerformance `segments` array
 * @param {string} sortBy - a segment field name, or `channelSortKey(channel)`
 * @returns {string|number|null}
 */
export const segmentSortValue = (segment, sortBy) => {
  if (sortBy.startsWith(CHANNEL_SORT_PREFIX)) {
    return segment.byChannel?.[sortBy.slice(CHANNEL_SORT_PREFIX.length)]?.value ?? null;
  }
  return segment[sortBy] ?? null;
};

/**
 * Order two column values. Missing ones (null conversions, or no size snapshot)
 * sort last in BOTH directions: flipping the direction is meant to surface the
 * other end of the measured rows, not to float a row with no data to the top.
 * @param {string|number|null} a
 * @param {string|number|null} b
 * @param {'asc'|'desc'} direction
 * @returns {number}
 */
export const compareSortValues = (a, b, direction) => {
  const aMissing = a == null;
  const bMissing = b == null;
  if (aMissing || bMissing) {
    if (aMissing && bMissing) {
      return 0;
    }
    return aMissing ? 1 : -1;
  }
  const ordering = typeof a === 'string' && typeof b === 'string' ? a.localeCompare(b) : a - b;
  return direction === 'desc' ? -ordering : ordering;
};

/**
 * Client-side sort of the fetched rows. Copies first so the fetched response is
 * never mutated, and relies on Array#sort being stable (ES2019) so rows that tie
 * — every row when sorting an all-null column — keep the API's order.
 * @param {Array<object>} [segments]
 * @param {string} sortBy
 * @param {'asc'|'desc'} direction
 * @returns {Array<object>}
 */
export const sortSegmentRows = (segments, sortBy, direction) =>
  [...(segments || [])].sort((a, b) =>
    compareSortValues(segmentSortValue(a, sortBy), segmentSortValue(b, sortBy), direction)
  );

/**
 * Direction to colour a size change with, matching SummaryCard: a move under 3%
 * of the starting size reads as flat rather than as growth or decline.
 * `sizeAtStart` can be null (or 0) while `sizeChange` has a value, in which case
 * there is no base to take a percentage of and the raw direction is shown.
 * @param {number|null} [sizeChange]
 * @param {number|null} [sizeAtStart]
 * @returns {'positive'|'negative'|'neutral'|null} `null` when there is nothing to indicate
 */
export const sizeChangeTone = (sizeChange, sizeAtStart) => {
  if (sizeChange == null) {
    return null;
  }
  if (sizeChange === 0) {
    return 'neutral';
  }
  if (sizeAtStart && Math.abs(sizeChange / sizeAtStart) * 100 < 3) {
    return 'neutral';
  }
  return sizeChange > 0 ? 'positive' : 'negative';
};

/**
 * Format a member/conversion count. A missing count is an em dash; 0 is 0.
 * @param {number|null} [value]
 * @returns {string}
 */
export const formatCount = (value) => (value == null ? EM_DASH : value.toLocaleString(navigator.language));

/**
 * Format a size change with an explicit sign so the direction survives being
 * read without the colour.
 * @param {number|null} [value]
 * @returns {string}
 */
export const formatSizeChange = (value) => {
  if (value == null) {
    return EM_DASH;
  }
  return `${value > 0 ? '+' : ''}${value.toLocaleString(navigator.language)}`;
};

/**
 * Format a conversion value in the domain currency. Negative values are real:
 * conversions are gross while value nets refunds, so a channel whose only
 * movement in the range was a refund arrives as a negative value. A missing
 * value is an em dash, same as the other formatters here — the segment-level
 * total is null-guarded by its caller, but a per-channel entry is not, so this
 * has to be safe on its own.
 * @param {number|null} [value]
 * @param {string} [currency] - ISO currency code from `state.domain.domain.currency`
 * @returns {string}
 */
export const formatConversionValue = (value, currency) =>
  value == null ? EM_DASH : value.toLocaleString(navigator.language, currency ? { style: 'currency', currency } : {});

/**
 * Explain, once for the whole table, why the size columns are blank. Size
 * history only started accruing when the snapshot stat was deployed, so a null
 * size is the expected state for most ranges right now and has to read as such
 * rather than as a failure.
 * @param {Array<object>} [segments]
 * @param {string|null} [earliestSnapshotDate] - ISO date the first size snapshot was taken
 * @returns {string|null} `null` when every row has its sizes
 */
export const describeSizeHistory = (segments, earliestSnapshotDate) => {
  const anyMissing = (segments || []).some(
    (segment) => segment.sizeChange == null || segment.sizeAtStart == null || segment.sizeAtEnd == null
  );
  if (!anyMissing) {
    return null;
  }
  if (!earliestSnapshotDate) {
    return 'Segment size history is not being recorded yet, so size change is unavailable for every segment.';
  }
  return `Segment size history started on ${DateTime.fromISO(earliestSnapshotDate).toFormat(
    'd LLL yyyy'
  )}. Size change is blank for any segment without a snapshot at both ends of the selected range.`;
};
